/* 2009-2018 Copyright Ridecell Inc. All rights reserved. */
/* globals fetch */
import 'whatwg-fetch'
import Cookies from 'js-cookie'
import URI from 'urijs'

import { handleError } from '../alert'
import { getStorage } from '../storage'
import { etagResponseHandler, getRequestHash } from './requestCache'
import { ensureIsMutable } from '../helpers'

function csrfSafeMethod(method) {
  // these HTTP methods do not require CSRF protection
  return /^(GET|HEAD|OPTIONS|TRACE)$/.test(method)
}

export async function buildRequestHeaders(opts) {
  const headers = { ...opts.headers }
  const storage = getStorage()
  if (storage) {
    try {
      const token = await storage.loadFromNamespace({ key: 'authToken' })
      if (token) {
        headers.Authorization = `JWT ${token}`
      }
    } catch (err) {
      // Ignore if token not found
    }
  }
  if (!csrfSafeMethod(opts.method)) {
    headers['X-CSRFToken'] = Cookies.get('csrftoken')
  }
  if (!headers.Accept) {
    headers.Accept = 'application/json'
  }
  return headers
}

export async function handleResponse(response, { shouldHandleError = true } = {}) {
  let json
  if (
    response.headers.has('Content-Type') &&
    response.headers.get('Content-Type').indexOf('application/json') > -1 &&
    response.status !== 204
  ) {
    json = await response.json()
  }
  if (!response.ok) {
    const error = {
      status: response.status,
    }
    if (json !== undefined) {
      error.data = json
      error.responseText = JSON.stringify(json)
    } else {
      error.responseText = await response.text()
    }
    if (shouldHandleError) {
      await handleError(error)
    }
    throw new Error(error.responseText)
  }
  if (json !== undefined) {
    return json
  }
  return response
}

export async function request(
  url,
  opts,
  { shouldHandleError = true } = {},
  credentials = 'same-origin'
) {
  const headers = await buildRequestHeaders(opts)

  const response = await fetch(url, {
    credentials,
    ...opts,
    headers,
  })

  const requestHash = getRequestHash({ ...opts, headers })
  const cachedResponse = etagResponseHandler(requestHash)(response)

  return handleResponse(cachedResponse, { shouldHandleError })
}

export function get(url, query = {}, { shouldHandleError = true } = {}) {
  const q = URI.parseQuery(new URI(url).query())
  return request(
    new URI(url).query({ ...q, ...query }).toString(),
    {
      method: 'GET',
    },
    { shouldHandleError }
  )
}

export function options(url, query = {}) {
  const q = URI.parseQuery(new URI(url).query())
  return request(new URI(url).query({ ...q, ...query }).toString(), {
    method: 'OPTIONS',
  })
}

export function put(url, data) {
  if (data instanceof FormData) {
    return request(url, {
      body: data,
      method: 'PUT',
    })
  }
  return request(url, {
    body: JSON.stringify(ensureIsMutable(data)),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'PUT',
  })
}

export function post(
  url,
  data = undefined,
  { shouldHandleError = true } = {},
  credentials = 'same-origin'
) {
  if (data instanceof FormData) {
    return request(url, {
      body: data,
      method: 'POST',
    })
  }
  return request(
    url,
    {
      body: data && JSON.stringify(ensureIsMutable(data)),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    },
    { shouldHandleError },
    credentials
  )
}

export function destroy(url, query = {}, hasBody = false) {
  if (hasBody) {
    const data = query
    if (data instanceof FormData) {
      return request(url, {
        body: data,
        method: 'DELETE',
      })
    }
    return request(url, {
      body: data && JSON.stringify(ensureIsMutable(data)),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    })
  }
  const q = URI.parseQuery(new URI(url).query())
  return request(new URI(url).query({ ...q, ...query }).toString(), {
    method: 'DELETE',
  })
}

export function patch(
  url,
  data,
  customHeaders = {},
  query = {},
  { shouldHandleError = true } = {}
) {
  const method = 'PATCH'
  const urlQuery = URI.parseQuery(new URI(url).query())
  const parsedUrl = new URI(url).query({ ...urlQuery, ...query }).toString()
  if (data instanceof FormData) {
    return request(parsedUrl, {
      body: data,
      headers: {
        ...customHeaders,
      },
      method,
    })
  }
  return request(
    parsedUrl,
    {
      body: JSON.stringify(ensureIsMutable(data)),
      headers: {
        'Content-Type': 'application/json',
        ...customHeaders,
      },
      method,
    },
    { shouldHandleError }
  )
}

export function getQueryString(queryParams) {
  return Object.keys(queryParams)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
    .join('&')
}
